import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import {
  useTheme,
  useMediaQuery,
  Box, 
  TextField, 
  Button, 
  Typography, 
  CircularProgress, 
  IconButton,
  Paper, 
  Chip, 
  Switch, 
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Add,
  Delete,
  ArrowBackIosNew,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import ErrorBoundary from 'components/ErrorBoundary';


const DEFAULT_COLORS = [
  '#4caf50', 
  '#8bc34a', 
  '#ffc107', 
  '#ff9800', 
  '#f44336'
];

const CompetencyFrameworkForm = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const { competencyFrameworkId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isNew = !competencyFrameworkId || competencyFrameworkId === 'new';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form, setForm] = useState({
    code: '',
    name: '',
    description: '',
    regulatoryRef: '',
    isActive: true,
    levels: [
      { level: 1, label: 'Awareness', description: '', color: '#f44336' },
      { level: 2, label: 'Understanding', description: '', color: '#ff9800' },
      { level: 3, label: 'Application', description: '', color: '#ffc107' },
      { level: 4, label: 'Mastery', description: '', color: '#4caf50' },
    ],
  });


  useEffect(() => {
    if (isNew) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/competencyframeworks/${competencyFrameworkId}`);
        setForm({
          code: d.code || '',
          name: d.name || '',
          description: d.description || '',
          regulatoryRef: d.regulatoryRef || '',
          isActive: d.isActive !== false,
          levels: d.levels || [],
        });
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [isNew, competencyFrameworkId]);

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const updateLevel = (index, field, value) =>
    setForm((prev) => ({
      ...prev,
      levels: prev.levels.map((l, i) => i === index ? { ...l, [field]: value } : l),
    }));

  const addLevel = () =>
    setForm((prev) => ({
      ...prev,
      levels: [
        ...prev.levels,
        {
          level: prev.levels.length + 1,
          label: '',
          description: '',
          color: DEFAULT_COLORS[prev.levels.length % DEFAULT_COLORS.length],
        },
      ],
    }));

  const removeLevel = (index) =>
    setForm((prev) => ({
      ...prev,
      levels: prev.levels
        .filter((_, i) => i !== index)
        .map((l, i) => ({ ...l, level: i + 1 })),
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code || !form.name) {
      toast.error('Code and name are required.');
      return;
    }
    try {
      setIsSubmitting(true);
      if (isNew) {
        await api.post('/competencyframeworks/new', form);
        toast.success('Framework created!');
      } else {
        await api.put(`/competencyframeworks/update/${competencyFrameworkId}`, form);
        toast.success('Framework updated!');
      }
      navigate('/competencyframeworks');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Save failed!');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <FlexCenter height={LAYOUT_HEIGHT}><CircularProgress /></FlexCenter>;
  if (error) return <FlexCenter height={LAYOUT_HEIGHT}><Typography color="error">{error.message}</Typography></FlexCenter>;

  return (
    <ErrorBoundary>
      <Box className="main-container-box">
        <FlexBetween>
          <Header
            title={'Competency Framework'}
            subtitle={`${loading ? 'Loading...' : competencyFrameworkId ? 'Update' : 'Create'}`}
          />
          {!loading && (
            <Box display="flex" alignItems="center" gap={1}>
              <StyledButton 
                onClick={() => navigate("/actypes")}
              >
                <ArrowBackIosNew />
              </StyledButton>
            </Box>
          )}
        </FlexBetween>

        <Box 
          component="form" 
          onSubmit={handleSubmit}
          height={LAYOUT_HEIGHT} 
          sx={{ overflowY: 'auto' }}
        >
          <Box 
            display="grid" 
            gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
            gap={2}         
            mt={1}   
          >
            <TextField
              size="small" 
              label="Code" 
              required
              value={form.code} 
              onChange={handleChange('code')}
              sx={{ gridColumn: 'span 2' }}
            />
            <TextField
              size="small" 
              label="Name" 
              required
              value={form.name} 
              onChange={handleChange('name')}
              sx={{ gridColumn: 'span 5' }}
            />
            <TextField
              size="small" 
              label="Regulatory Reference (e.g. EASA Part-FCL)"
              value={form.regulatoryRef} 
              onChange={handleChange('regulatoryRef')}
              sx={{ gridColumn: 'span 5' }}
            />
            
            <TextField
              size="small" 
              multiline 
              minRows={2} 
              label="Description"
              value={form.description} 
              onChange={handleChange('description')}
              sx={{ gridColumn: isMobile ? 'span 10' : 'span 11' }}
            />

            <Box 
              sx={{ 
                gridColumn: isMobile ? 'span 2' : 'span 1', 
                display: 'flex', 
                alignItems: 'center' 
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                    size="small"
                  />
                }
                label={<Typography variant="caption">Active</Typography>}
              />
            </Box>

            {/* Proficiency Levels */}
            <Box sx={{ gridColumn: 'span 12' }}>
              <Divider sx={{ mb: 1 }} />
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="space-between" 
                mb={1.5}
              >
                <Typography variant="subtitle2">Proficiency Levels</Typography>
                <Button 
                  variant="outlined"
                  size="small" 
                  startIcon={<Add />} 
                  onClick={addLevel} 
                >
                  Add Level
                </Button>
              </Box>

              <Box display="flex" flexDirection="column" gap={1.5}>
                {form.levels.map((level, index) => (
                  <Paper 
                    key={index} 
                    elevation={0} 
                    sx={{ 
                      p: 1, 
                      border: 1, 
                      borderColor: 'divider', 
                      borderRadius: 1 
                    }}
                  >
                    <Box 
                      display="flex" 
                      gap={1.5} 
                      alignItems="flex-start"
                    >                      
                      <Chip
                        label={`L${level.level}`}
                        size="small"
                        sx={{ 
                          mt: 1, 
                          flexShrink: 0, 
                          bgcolor: level.color, 
                          color: 'text.primary' 
                        }}
                      />
                      <TextField
                        size="small" 
                        label="Label" 
                        required
                        value={level.label}
                        onChange={(e) => updateLevel(index, 'label', e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size="small" 
                        label="Description"
                        value={level.description}
                        onChange={(e) => updateLevel(index, 'description', e.target.value)}
                        sx={{ flex: 2 }}
                      />
                      <TextField
                        size="small" 
                        type="color" 
                        label="Color"
                        value={level.color}
                        onChange={(e) => updateLevel(index, 'color', e.target.value)}
                        sx={{ width: 80 }}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                      <IconButton
                        size="small" 
                        color="error"
                        onClick={() => removeLevel(index)}
                        disabled={form.levels.length <= 1}
                        sx={{ mt: 0.5 }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>

            <Button
              type="submit" variant="contained" color="secondary"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ gridColumn: 'span 12', height: 42 }}
            >
              {isSubmitting ? 'Saving…' : isNew ? 'Create' : 'Update'}
            </Button>
          </Box>
        </Box>
      </Box>
    </ErrorBoundary>
  );
};

export default CompetencyFrameworkForm;
